/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {getCSSVariableValue} from '../../../assets/ts/_utils'
import {useThemeMode} from '../../layout/theme-mode/ThemeModeProvider'
import {Link} from 'react-router-dom'

type Props = {
  className?: string
  project: {
    id?: string
    name: string
    clientName?: string
    client_id: string
    total_time_percentage?: number
    cycle?: number
    used_time?: number
    budgeted_hours?: number
    cost?: number
    budget?: number
    cost_percentage?: number
  }
}

const RecurringProjectWidget: React.FC<Props> = ({className = '', project}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()

  // Usar % de custo quando houver orçamento em R$; senão % de horas
  const displayPercentage =
    project.budget != null && project.budget > 0 && project.cost_percentage !== undefined
      ? project.cost_percentage
      : (project.total_time_percentage ?? 0)

  const getChartColor = (percentage: number) => {
    if (percentage >= 90) return 'danger'
    if (percentage >= 75) return 'warning'
    return 'success'
  }

  const getStatusText = (percentage: number) => {
    if (percentage >= 90) return 'Crítico'
    if (percentage >= 75) return 'Atenção'
    return 'Normal'
  }

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chartColor = getChartColor(displayPercentage)
    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartColor, displayPercentage)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, displayPercentage])

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <div className='card-title d-flex flex-column'>
          <div className='d-flex align-items-center'>
            <div className='d-flex flex-column'>
              <Link
                to={`/clients/${project.client_id}`}
                className='fs-5 fw-bold text-dark text-hover-primary'
              >
                [{project.clientName || 'Cliente'}]
              </Link>
              <Link
                to={`/projects/${project.id}`}
                className='fs-6 fw-semibold text-muted text-hover-primary'
              >
                {project.name}
              </Link>
              <div className='mt-2'>
                <span className={`badge badge-light-${getChartColor(displayPercentage)} fw-bold`}>
                  {getStatusText(displayPercentage)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end::Header */}
      
      {/* begin::Body */}
      <div className='card-body pt-2 pb-4 d-flex flex-wrap align-items-center justify-content-center'>
        {/* begin::Chart */}
        <div className='d-flex flex-center flex-shrink-0'>
          <div ref={chartRef} style={{width: '180px', height: '180px'}} />
        </div>
        {/* ícone discreto de info: tooltip com ciclo, custo e orçamento (padrão em gráficos) */}
        <OverlayTrigger
          placement='right'
          delay={{show: 100, hide: 100}}
          overlay={
            <Tooltip id={`recurring-project-tooltip-${project.id}`}>
              <div className='text-start'>
                <div>Dia de renovação do ciclo: {project.cycle ?? 1}</div>
                <div>
                  {project.cost != null
                    ? new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(project.cost)
                    : '-'}{' '}
                  Custo lançado
                </div>
                <div>
                  {project.budget != null
                    ? new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(project.budget)
                    : '-'}{' '}
                  Orçamento ciclo
                </div>
              </div>
            </Tooltip>
          }
        >
          <span
            className='text-gray-500 text-hover-primary flex-shrink-0 ms-1 d-inline-flex align-items-center justify-content-center'
            style={{cursor: 'help', lineHeight: 1}}
            role='img'
            aria-label='Detalhes do ciclo'
          >
            <i className='bi bi-info-circle' style={{fontSize: '0.95rem'}} />
          </span>
        </OverlayTrigger>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {RecurringProjectWidget}

const chartOptions = (chartColor: string, percentage: number): ApexOptions => {
  const baseColor = getCSSVariableValue('--kt-' + chartColor)
  const lightColor = getCSSVariableValue('--kt-' + chartColor + '-light')
  const labelColor = getCSSVariableValue('--kt-gray-700')

  return {
    series: [percentage],
    chart: {
      fontFamily: 'inherit',
      height: 180,
      type: 'radialBar',
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: '65%',
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            color: labelColor,
            fontSize: '28px',
            fontWeight: '700',
            offsetY: 8,
            show: true,
            formatter: function (val) {
              return val + '%'
            },
          },
        },
        track: {
          background: lightColor,
          strokeWidth: '97%',
          margin: 0,
        },
      },
    },
    fill: {
      type: 'solid',
      colors: [baseColor],
    },
    stroke: {
      lineCap: 'round',
    },
    labels: ['Progress'],
  }
} 